/**
 * Utilidad para detectar solicitudes que coinciden con empresas ya registradas.
 * Compara RUT, teléfono y sitio web normalizados (sin índices compuestos en Firestore).
 */

/**
 * Normaliza RUT chileno para comparación: quita puntos, guiones y espacios; deja dígitos y 'k'.
 * @param {string} rut
 * @returns {string}
 */
export function normalizeRut(rut) {
  if (!rut || typeof rut !== 'string') return '';
  const s = rut.trim().toLowerCase().replace(/[\s.]/g, '').replace(/-/g, '');
  return s;
}

/**
 * Normaliza teléfono para comparación: solo dígitos.
 * @param {string} phone
 * @returns {string}
 */
export function normalizePhone(phone) {
  if (!phone || typeof phone !== 'string') return '';
  return String(phone).replace(/\D/g, '');
}

/**
 * Normaliza URL de sitio web para comparación: minúsculas, sin protocolo, sin www, sin barra final.
 * @param {string} url
 * @returns {string}
 */
export function normalizeWebsite(url) {
  if (!url || typeof url !== 'string') return '';
  let s = url.trim().toLowerCase();
  s = s.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '').split('?')[0];
  return s;
}

/**
 * Extrae RUT de un documento (solicitud o empresa).
 * @param {Object} doc
 * @returns {string}
 */
function getRut(doc) {
  return (doc.rut_empresa || doc.rut || doc.rutEmpresa || '').trim();
}

/**
 * Extrae teléfono de empresa de un documento.
 * @param {Object} doc
 * @returns {string}
 */
function getPhone(doc) {
  return (doc.telefono_empresa || doc.telefono || doc.telefonoEmpresa || '').trim();
}

/**
 * Extrae sitio web de un documento.
 * @param {Object} doc
 * @returns {string}
 */
function getWeb(doc) {
  return (doc.web_actual || doc.web || doc.web_empresa || doc.sitioWeb || '').trim();
}

/**
 * Indica si una empresa registrada coincide con una solicitud por al menos un criterio
 * (RUT, teléfono o web normalizados). No considera email para evitar falsos positivos por
 * emails genéricos.
 * @param {Object} empresa - Documento de empresa (id + data)
 * @param {Object} solicitud - Documento de solicitud (id + data)
 * @returns {{ match: boolean, byRut: boolean, byPhone: boolean, byWeb: boolean }}
 */
export function empresaMatchesSolicitud(empresa, solicitud) {
  const rutE = normalizeRut(getRut(empresa));
  const rutS = normalizeRut(getRut(solicitud));
  const phoneE = normalizePhone(getPhone(empresa));
  const phoneS = normalizePhone(getPhone(solicitud));
  const webE = normalizeWebsite(getWeb(empresa));
  const webS = normalizeWebsite(getWeb(solicitud));

  const byRut = rutE.length > 0 && rutS.length > 0 && rutE === rutS;
  const byPhone = phoneE.length >= 6 && phoneS.length >= 6 && phoneE === phoneS;
  const byWeb = webE.length > 0 && webS.length > 0 && webE === webS;

  return {
    match: byRut || byPhone || byWeb,
    byRut,
    byPhone,
    byWeb
  };
}

/**
 * Dado una lista de empresas y una solicitud, devuelve las empresas que coinciden,
 * con el motivo del match.
 * @param {Array<{ id: string, ... }>} empresas - Lista de documentos de empresas
 * @param {Object} solicitud - Documento de solicitud
 * @returns {Array<{ empresa: { id, ... }, byRut: boolean, byPhone: boolean, byWeb: boolean }>}
 */
export function findEmpresasMatchingSolicitud(empresas, solicitud) {
  if (!Array.isArray(empresas) || !solicitud) return [];
  const results = [];
  const seen = new Set();
  for (const emp of empresas) {
    if (seen.has(emp.id)) continue;
    const { match, byRut, byPhone, byWeb } = empresaMatchesSolicitud(emp, solicitud);
    if (match) {
      seen.add(emp.id);
      results.push({ empresa: emp, byRut, byPhone, byWeb });
    }
  }
  return results;
}
